import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useTranslation } from 'next-i18next'
import { StaticImageData } from 'next/image'
import React, { ReactNode } from 'react'
import FAQAccordion, { FAQSurface } from 'src/components/FAQ/FAQAccordion'
import useWhatsappCityHint from 'src/hooks/useWhatsappCityHint'
import { appendCityHint, buildWhatsappUrl } from 'src/utils/whatsapp'

export type { FAQSurface }

export interface FAQItem {
  title: string
  description: string
  /** Optional rendered body — when present, replaces the plain text
   *  paragraph (used for entries with inline links). */
  descriptionNode?: ReactNode
}

type IProps = {
  title: string
  subtitle?: string
  /** Legacy — ignored. Retained so call sites don't break. */
  imageAlt?: string
  faqs: FAQItem[]
  /** Legacy — ignored. Retained so call sites don't break. */
  image?: StaticImageData | string
  className?: string
  titleAs?: 'h2' | 'h3'
  /** Pre-filled WhatsApp message for the "no encuentras tu respuesta" link.
   *  Each call site passes a surface-specific message so the agent lands in
   *  WhatsApp with context (which pillar/program/page the user was on). */
  whatsappMsg?: string
  /** Hide the "¿No encuentras tu respuesta? Chatéanos en WhatsApp" link —
   *  for audiences that must not be routed to the chatbot line. */
  hideWhatsapp?: boolean
  /** Required — identifies which page mounted the FAQ for the `faq_expand`
   *  PostHog event. See `FAQSurface` for valid values. */
  surface: FAQSurface
}

const FAQ = ({
  title,
  subtitle,
  faqs,
  className = '',
  titleAs = 'h3',
  whatsappMsg,
  hideWhatsapp = false,
  surface
}: IProps) => {
  const Heading = titleAs
  const { t } = useTranslation('common')
  const cityHintLine = useWhatsappCityHint()
  const whatsappUrl = buildWhatsappUrl(
    appendCityHint(whatsappMsg, cityHintLine)
  )
  return (
    <section
      className={`w-full bg-cream-300 py-16 md:py-24 ${className}`}
      aria-labelledby="faq-heading"
    >
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-5 md:grid-cols-12 md:gap-16 md:px-10">
        {/* Left rail — editorial heading, no illustration */}
        <div className="md:col-span-4 md:pt-2">
          <p className="font-display text-sm tracking-[0.2em] text-blue-500 uppercase">
            {t('FAQ_BLOCK.EYEBROW')}
          </p>
          <Heading
            id="faq-heading"
            className="mt-4 font-display text-4xl leading-[1.05] font-medium text-ink-700 md:text-5xl"
          >
            {title}
          </Heading>
          {subtitle ? (
            <p className="mt-5 max-w-xs text-base leading-relaxed text-ink-200">
              {subtitle}
            </p>
          ) : null}

          {!hideWhatsapp && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-3 border-t border-ink-100/40 pt-5 text-sm font-medium text-ink-700 transition-colors hover:text-blue-500"
            >
              <WhatsAppIcon className="text-lg text-blue-500" />
              <span>
                {/* Trailing space folded into the template literal rather than a
                  `{' '}` spacer — an expression next to `{' '}` makes React 19
                  emit a `<!-- -->` boundary comment, which FB IAB strips before
                  hydration → #418. See CLAUDE.md "Trigger H". */}
                {`${t('FAQ_BLOCK.NO_ANSWER')} `}
                <span className="underline decoration-blue-500/40 decoration-1 underline-offset-[6px] group-hover:decoration-blue-500">
                  {t('FAQ_BLOCK.CHAT_WHATSAPP')}
                </span>
              </span>
            </a>
          )}
        </div>

        {/* Accordion column */}
        <div className="md:col-span-8">
          <ul className="border-t border-ink-100/30">
            {faqs.map(
              ({ title: itemTitle, description, descriptionNode }, index) => (
                <li key={`faq-${index}`} className="group">
                  <FAQAccordion
                    title={itemTitle}
                    description={description}
                    descriptionNode={descriptionNode}
                    index={index}
                    defaultExpanded={index === 0}
                    surface={surface}
                  />
                </li>
              )
            )}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default React.memo(FAQ)
